import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

export type FeedbackType = 'bug' | 'feature' | 'general';
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

interface NavigationEntry {
  path: string;
  timestamp: number;
  title: string;
}

interface ConsoleError {
  message: string;
  timestamp: number;
  stack?: string;
}

interface SessionData {
  startTime: number;
  navigationHistory: NavigationEntry[];
  consoleErrors: ConsoleError[];
  browser: string;
  os: string;
  screenResolution: string;
  currentPage: string;
  sessionDuration: number;
}

interface FeedbackContextType {
  sessionData: SessionData;
  captureScreenshot: () => Promise<string | null>;
  clearConsoleErrors: () => void;
  getContextData: () => {
    user: any;
    session: SessionData;
    environment: {
      userAgent: string;
      language: string;
      timezone: string;
    };
  };
  isModalOpen: boolean;
  openFeedbackModal: (type?: FeedbackType, message?: string) => void;
  closeFeedbackModal: () => void;
  prefilledType?: FeedbackType;
  prefilledMessage?: string;
}

const FeedbackContext = createContext<FeedbackContextType | undefined>(undefined);

export const useFeedbackContext = () => {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedbackContext must be used within a FeedbackProvider');
  }
  return context;
};

const getBrowserInfo = (): string => {
  const ua = navigator.userAgent;
  if (ua.indexOf('Chrome') > -1) return 'Chrome';
  if (ua.indexOf('Safari') > -1) return 'Safari';
  if (ua.indexOf('Firefox') > -1) return 'Firefox';
  if (ua.indexOf('Edge') > -1) return 'Edge';
  return 'Unknown';
};

const getOSInfo = (): string => {
  const platform = navigator.platform;
  if (platform.indexOf('Win') > -1) return 'Windows';
  if (platform.indexOf('Mac') > -1) return 'macOS';
  if (platform.indexOf('Linux') > -1) return 'Linux';
  if (platform.indexOf('iPhone') > -1 || platform.indexOf('iPad') > -1) return 'iOS';
  if (platform.indexOf('Android') > -1) return 'Android';
  return 'Unknown';
};

export const FeedbackProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const sessionStartRef = useRef<number>(Date.now());
  const originalConsoleError = useRef<typeof console.error>(console.error);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [prefilledType, setPrefilledType] = useState<FeedbackType | undefined>();
  const [prefilledMessage, setPrefilledMessage] = useState<string | undefined>();
  
  const [sessionData, setSessionData] = useState<SessionData>({
    startTime: Date.now(),
    navigationHistory: [],
    consoleErrors: [],
    browser: getBrowserInfo(),
    os: getOSInfo(),
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    currentPage: location.pathname,
    sessionDuration: 0,
  });

  // Track navigation history
  useEffect(() => {
    const newEntry: NavigationEntry = {
      path: location.pathname,
      timestamp: Date.now(),
      title: document.title,
    };

    setSessionData(prev => ({
      ...prev,
      currentPage: location.pathname,
      navigationHistory: [...prev.navigationHistory.slice(-4), newEntry], // Keep last 5 entries
      sessionDuration: Math.floor((Date.now() - sessionStartRef.current) / 1000),
    }));
  }, [location]);

  // Intercept console errors
  useEffect(() => {
    const originalError = originalConsoleError.current;
    
    console.error = (...args: any[]) => {
      const errorMessage = args
        .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
        .join(' ');
      
      const error: ConsoleError = {
        message: errorMessage,
        timestamp: Date.now(),
        stack: new Error().stack,
      };

      setSessionData(prev => ({
        ...prev,
        consoleErrors: [...prev.consoleErrors.slice(-9), error], // Keep last 10 errors
      }));

      // Call original console.error
      originalError(...args);
    };

    // Cleanup
    return () => {
      console.error = originalError;
    };
  }, []);

  // Update session duration periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionData(prev => ({
        ...prev,
        sessionDuration: Math.floor((Date.now() - sessionStartRef.current) / 1000),
      }));
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  const captureScreenshot = async (): Promise<string | null> => {
    try {
      // Check if the browser supports the necessary APIs
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        console.warn('Screenshot capture not supported in this browser');
        return null;
      }

      // Request screen capture permission
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: 'screen' } as any,
      });

      // Create video element to capture frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      // Wait for video to load
      await new Promise(resolve => {
        video.onloadedmetadata = resolve;
      });

      // Create canvas and capture frame
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0);

      // Stop the stream
      stream.getTracks().forEach(track => track.stop());

      // Convert to base64
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      return null;
    }
  };

  const clearConsoleErrors = () => {
    setSessionData(prev => ({
      ...prev,
      consoleErrors: [],
    }));
  };

  const getContextData = () => {
    return {
      user: user ? {
        id: user.id,
        email: user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        organizationId: user.organizationId,
        organizationName: user.organizationName,
        role: user.role,
      } : null,
      session: {
        ...sessionData,
        sessionDuration: Math.floor((Date.now() - sessionStartRef.current) / 1000),
      },
      environment: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    };
  };

  const openFeedbackModal = (type?: FeedbackType, message?: string) => {
    setPrefilledType(type);
    setPrefilledMessage(message);
    setIsModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setIsModalOpen(false);
    setPrefilledType(undefined);
    setPrefilledMessage(undefined);
  };

  const value: FeedbackContextType = {
    sessionData,
    captureScreenshot,
    clearConsoleErrors,
    getContextData,
    isModalOpen,
    openFeedbackModal,
    closeFeedbackModal,
    prefilledType,
    prefilledMessage,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
};